import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, Plus } from 'lucide-react';
import { brandsApi } from './api';
import { BrandCard } from './BrandCard';
import { NewBrandDialog } from './NewBrandDialog';
import { queryKeys } from '@/lib/api/queryKeys';
import type { XkBrand } from '@/lib/api/types';
import { useAuth } from '@/lib/auth/useAuth';
import { usePlanQuota } from '@/lib/permissions/usePlanQuota';
import { useBrandContextStore } from '@/lib/branding/brandContextStore';
import { useT } from '@/lib/i18n/useT';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpHint } from '@/features/help/HelpHint';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { useFavoritable } from '@/features/favorites/FavoritableContext';

/**
 * `/brands` — todas las marcas a las que llega el usuario, en tarjetas
 * pintadas con su identidad y con lo que tiene cada una. Se llega desde el
 * selector de marca del topbar («Todas las marcas», junto a «Nuevo brand»).
 *
 * Dos consultas: la lista de marcas, que ya tiene el selector en caché, y
 * los contadores de todas en una sola llamada (`/xk-company/summary`); el
 * kit de cada una lo pide su tarjeta.
 */
export function BrandsGalleryPage() {
  const t = useT();
  const navigate = useNavigate();
  useFavoritable(t('brandsGallery.title'));
  const { isPending } = useAuth();
  const canCreateBrand = !isPending;
  const brandQuota = usePlanQuota('maxBrands');
  const setSelectedBrandId = useBrandContextStore((s) => s.setSelectedBrandId);
  const [newOpen, setNewOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const brandsQ = useQuery({ queryKey: queryKeys.brands.all, queryFn: () => brandsApi.list() });
  const summaryQ = useQuery({
    queryKey: queryKeys.brands.summary,
    queryFn: () => brandsApi.summary(),
    staleTime: 30_000,
  });

  // La raíz del workspace primero; el resto por nombre.
  const brands = useMemo(() => {
    const items = [...(brandsQ.data?.items ?? [])];
    return items.sort((a, b) => {
      const ra = a.parentId == null ? 0 : 1;
      const rb = b.parentId == null ? 0 : 1;
      return ra - rb || (a.name ?? '').localeCompare(b.name ?? '');
    });
  }, [brandsQ.data]);
  const summaryById = useMemo(
    () => new Map((summaryQ.data ?? []).map((row) => [row.brandId, row])),
    [summaryQ.data],
  );

  const openCreate = () => {
    if (brandQuota.reached) setUpgradeOpen(true);
    else setNewOpen(true);
  };
  const handleCreated = (brand: XkBrand) => {
    setSelectedBrandId(brand.id);
    setNewOpen(false);
    navigate(`/brand-studio/${brand.id}`);
  };

  return (
    <div className="page space-y-6">
      <Breadcrumbs items={[{ label: t('brandsGallery.title') }]} />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
            <LayoutGrid className="h-5 w-5" />
            {t('brandsGallery.title')}
            <HelpHint section="brands-gallery" />
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-[var(--color-fg-muted)]">{t('brandsGallery.subtitle')}</p>
        </div>
        {canCreateBrand && (
          <Button variant="primary" onClick={openCreate}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('brand.new.cta')}
          </Button>
        )}
      </header>

      {brandsQ.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64 rounded-2xl" />
          ))}
        </div>
      ) : brands.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[var(--color-border-default)] p-8 text-center text-sm text-[var(--color-fg-muted)]">
          {t('brandsGallery.empty')}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {brands.map((brand) => (
            <BrandCard key={brand.id} brand={brand} summary={summaryById.get(brand.id)} />
          ))}
        </div>
      )}

      <NewBrandDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={handleCreated}
        onQuotaExceeded={() => setUpgradeOpen(true)}
      />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="maxBrands"
        currentPlanName={brandQuota.planName}
        limit={brandQuota.limit}
      />
    </div>
  );
}
